import React from "react";
import {
  FaCalendarAlt,
  FaBalanceScale,
  FaShieldAlt,
  FaFileAlt,
  FaClock,
  FaCreditCard,
} from "react-icons/fa";

const ServiceAgreement: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Service Agreement</h1>
          <p className="text-gray-600">
            WhatisHyped Media LLC (DBA WIH Media) - Professional Media Services (Florida)
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last Updated: February 5, 2026 | Governed by Florida Law
          </p>
        </div>

        {/* Quick Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-blue-900 mb-3">Agreement Summary</h2>
          <p className="text-blue-800 text-sm leading-relaxed">
            This Service Agreement governs all services provided by WhatisHyped Media LLC (doing
            business as WIH Media). By booking services, making a payment, or accepting
            deliverables, you agree to these terms. Please read carefully and contact us with any
            questions.
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          {/* Section 1 */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <FaFileAlt className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">1. Services Provided</h2>
                <div className="space-y-3 text-gray-700">
                  <p>
                    WhatisHyped Media LLC, doing business as WIH Media ("WIH Media," "we," "us,"
                    "our"), provides professional media services including but not limited to:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Real Estate Photography (interior and exterior)</li>
                    <li>Aerial/Drone Photography and Videography</li>
                    <li>Website Creation and Development</li>
                    <li>Software Engineering and Technical Consulting</li>
                    <li>Promotional Video Production</li>
                  </ul>
                  <p>
                    Services are provided as described in the booked package or custom quote. Any
                    modifications must be agreed upon in writing.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="border-t pt-8">
            <div className="flex items-start gap-3 mb-4">
              <FaCalendarAlt className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">2. Booking and Scheduling</h2>
                <div className="space-y-3 text-gray-700">
                  <p>
                    <strong>Booking:</strong> Services are booked through our website, email, or
                    phone. A booking is confirmed upon receipt of payment or signed agreement.
                  </p>
                  <p>
                    <strong>Scheduling:</strong> We will coordinate with you to schedule services at
                    a mutually agreed time. For real estate and drone photography, weather and
                    lighting conditions may affect scheduling.
                  </p>
                  <p>
                    <strong>Cancellation Policy:</strong> Cancellations must be made at least 48
                    hours before the scheduled service. Cancellations within 48 hours may be subject
                    to a 50% cancellation fee. Same-day cancellations are non-refundable. In
                    accordance with Florida consumer protection laws, cancellations due to weather
                    emergencies (hurricanes, tropical storms, floods) will receive full credit or
                    reschedule at no charge.
                  </p>
                  <p>
                    <strong>Rescheduling:</strong> One free reschedule is allowed with 48 hours
                    notice. Additional reschedules may incur a $50 rescheduling fee. Weather-related
                    rescheduling is always free.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="border-t pt-8">
            <div className="flex items-start gap-3 mb-4">
              <FaCreditCard className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">3. Payment Terms</h2>
                <div className="space-y-3 text-gray-700">
                  <p>
                    <strong>Payment Methods:</strong> We accept credit/debit cards, ACH transfers
                    (via Stripe), checks payable to "WhatisHyped Media LLC" or "WIH Media," and wire
                    transfers.
                  </p>
                  <p>
                    <strong>Payment Schedule:</strong> Payment is due according to the invoice terms
                    (typically Net 30). For large projects, a 50% deposit may be required before
                    work begins.
                  </p>
                  <p>
                    <strong>Late Payments:</strong> Invoices not paid within the stated terms are
                    subject to a late fee of 1.5% per month (18% annually), the maximum rate
                    permitted under Florida Statute §687.03. After 60 days, unpaid accounts may be
                    referred to collections.
                  </p>
                  <p>
                    <strong>Refunds:</strong> In accordance with Florida's consumer protection
                    standards, refunds are evaluated on a case-by-case basis. Services already
                    rendered are non-refundable. Deposits for cancelled projects may be refunded
                    less any work completed, expenses incurred, and administrative fees. Clients
                    have the right to dispute charges through their payment provider or contact the
                    Florida Department of Agriculture and Consumer Services.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="border-t pt-8">
            <div className="flex items-start gap-3 mb-4">
              <FaClock className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  4. Delivery and Turnaround
                </h2>
                <div className="space-y-3 text-gray-700">
                  <p>
                    <strong>Standard Turnaround:</strong> Unless otherwise specified, photography
                    deliverables are provided within 24-48 hours. Video and development projects
                    have custom timelines outlined in the agreement.
                  </p>
                  <p>
                    <strong>Rush Delivery:</strong> Rush delivery services are available for an
                    additional fee and must be requested at the time of booking.
                  </p>
                  <p>
                    <strong>Delivery Method:</strong> Digital deliverables are provided via secure
                    download link or cloud storage. Physical media can be provided upon request for
                    an additional fee.
                  </p>
                  <p>
                    <strong>File Retention:</strong> We retain project files for 90 days after
                    delivery. After 90 days, files may be archived or deleted. Extended storage is
                    available upon request.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="border-t pt-8">
            <div className="flex items-start gap-3 mb-4">
              <FaShieldAlt className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  5. Usage Rights and Licensing
                </h2>
                <div className="space-y-3 text-gray-700">
                  <p>
                    <strong>Copyright:</strong> WIH Media retains copyright to all original creative
                    work. Clients receive a non-exclusive, perpetual license to use deliverables for
                    their intended business purpose.
                  </p>
                  <p>
                    <strong>Portfolio Use:</strong> We reserve the right to use completed work in
                    our portfolio, marketing materials, and social media unless otherwise agreed in
                    writing.
                  </p>
                  <p>
                    <strong>Third-Party Use:</strong> Clients may not resell, redistribute, or
                    sublicense our work to third parties without written permission.
                  </p>
                  <p>
                    <strong>Source Code:</strong> For software/website projects, clients receive
                    full source code ownership unless otherwise specified. Open-source components
                    remain under their respective licenses.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section className="border-t pt-8">
            <div className="flex items-start gap-3 mb-4">
              <FaBalanceScale className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  6. Liability and Warranties
                </h2>
                <div className="space-y-3 text-gray-700">
                  <p>
                    <strong>Liability Limitation:</strong> WhatisHyped Media LLC's (DBA WIH Media)
                    liability is limited to the total amount paid for services. We are not liable
                    for indirect, incidental, consequential, or punitive damages.
                  </p>
                  <p>
                    <strong>Property Damage:</strong> We carry general liability insurance. Any
                    damage to client property caused by our negligence will be covered by our
                    insurance up to policy limits.
                  </p>
                  <p>
                    <strong>Drone Operations:</strong> All drone operations are conducted by FAA
                    Part 107 certified pilots in compliance with federal regulations and Florida
                    state laws. We carry drone liability insurance. Clients must ensure compliance
                    with local Florida ordinances and homeowner association rules. Certain areas in
                    Florida (near airports, military bases, national parks) require special
                    authorization.
                  </p>
                  <p>
                    <strong>Quality Guarantee:</strong> We guarantee professional quality work. If
                    you are not satisfied, we will provide reasonable revisions at no additional
                    cost. Extensive revisions beyond the original scope may incur additional fees.
                  </p>
                  <p>
                    <strong>Technical Issues:</strong> While we strive for error-free work,
                    technical issues may arise. For software/web projects, we provide a 30-day
                    warranty for bug fixes related to original specifications.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 7 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Client Responsibilities</h2>
            <div className="space-y-3 text-gray-700">
              <p>Clients agree to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Provide accurate property/project information and access as needed</li>
                <li>
                  Ensure properties are ready for photography/video (clean, staged, lights on, etc.)
                </li>
                <li>Obtain necessary permissions for drone flights, if applicable</li>
                <li>Provide timely feedback and approvals to avoid project delays</li>
                <li>Respect our photographers' and developers' time and safety</li>
                <li>Provide accurate billing information and maintain current contact details</li>
              </ul>
            </div>
          </section>

          {/* Section 8 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Confidentiality</h2>
            <div className="space-y-3 text-gray-700">
              <p>
                We respect client confidentiality. Any sensitive business information, unreleased
                properties, or proprietary data shared with us will be kept confidential and not
                disclosed to third parties.
              </p>
              <p>
                For software projects, we can sign Non-Disclosure Agreements (NDAs) upon request.
              </p>
            </div>
          </section>

          {/* Section 9 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              9. Force Majeure and Weather Events
            </h2>
            <div className="space-y-3 text-gray-700">
              <p>
                WhatisHyped Media LLC (DBA WIH Media) is not liable for failure to perform services
                due to circumstances beyond our reasonable control, including but not limited to:
                hurricanes, tropical storms, floods, tornadoes, natural disasters, severe weather,
                equipment failure, illness, government restrictions, mandatory evacuations, or other
                acts of God.
              </p>
              <p>
                <strong>Florida Weather Considerations:</strong> Given Florida's hurricane season
                (June 1 - November 30) and frequent severe weather, we reserve the right to postpone
                services for safety reasons. Drone operations are particularly weather-sensitive and
                require favorable wind and visibility conditions.
              </p>
              <p>
                In cases of force majeure, we will reschedule services at no additional charge or
                provide a full refund if rescheduling is not feasible within a reasonable timeframe.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              10. Dispute Resolution and Governing Law
            </h2>
            <div className="space-y-3 text-gray-700">
              <p>
                <strong>Informal Resolution:</strong> We encourage clients to contact us directly
                with any concerns. Most issues can be resolved through open communication.
              </p>
              <p>
                <strong>Mediation:</strong> If a dispute cannot be resolved informally, both parties
                agree to attempt good-faith mediation before pursuing legal action. Mediation costs
                will be shared equally between parties.
              </p>
              <p>
                <strong>Governing Law:</strong> This agreement is governed by and construed in
                accordance with the laws of the State of Florida, without regard to its conflict of
                law provisions. This agreement complies with Florida's Deceptive and Unfair Trade
                Practices Act (Florida Statute Chapter 501, Part II).
              </p>
              <p>
                <strong>Venue and Jurisdiction:</strong> Any legal proceedings arising from this
                agreement shall be brought exclusively in the state or federal courts located in
                Miami-Dade County, Florida. Both parties consent to the personal jurisdiction of
                these courts.
              </p>
              <p>
                <strong>Consumer Rights:</strong> Nothing in this agreement waives your rights under
                Florida consumer protection laws. You may file complaints with the Florida
                Department of Agriculture and Consumer Services at 1-800-HELP-FLA (1-800-435-7352)
                or www.fdacs.gov.
              </p>
            </div>
          </section>

          {/* Section 11 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              11. Modifications to Agreement
            </h2>
            <div className="space-y-3 text-gray-700">
              <p>
                WhatisHyped Media LLC (DBA WIH Media) reserves the right to modify this Service
                Agreement at any time. Updated terms will be posted on our website with a new "Last
                Updated" date.
              </p>
              <p>
                Changes apply to services booked after the update date. Existing bookings are
                governed by the terms in effect at the time of booking.
              </p>
            </div>
          </section>

          {/* Section 12 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
            <div className="space-y-2 text-gray-700">
              <p>
                <strong>Legal Name:</strong> WhatisHyped Media LLC
                <br />
                <strong>DBA:</strong> WIH Media
                <br />
                <strong>State:</strong> Florida
              </p>
              <p>
                <strong>Principal Office:</strong> Sebastian, FL 32958
              </p>
              <p>
                <strong>Email:</strong>{" "}
                <a href="mailto:billing@wihmedia.com" className="text-blue-600 hover:underline">
                  billing@wihmedia.com
                </a>
              </p>
              <p>
                <strong>Support:</strong>{" "}
                <a href="mailto:support@wihmedia.com" className="text-blue-600 hover:underline">
                  support@wihmedia.com
                </a>
              </p>
              <p>
                <strong>Phone:</strong> (555) 123-4567
              </p>
              <p>
                <strong>Website:</strong>{" "}
                <a href="https://wihmedia.com" className="text-blue-600 hover:underline">
                  https://wihmedia.com
                </a>
              </p>
              <p>
                <strong>FAA Drone Certificate:</strong> Part 107 Remote Pilot Certificate (Required
                for commercial drone operations in Florida)
              </p>
            </div>
          </section>

          {/* Acceptance */}
          <section className="border-t pt-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By booking services, making a payment, or accepting deliverables from WhatisHyped
                Media LLC (DBA WIH Media), you acknowledge that you have read, understood, and agree
                to be bound by this Service Agreement. If you do not agree to these terms, please do
                not use our services.
              </p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm mt-8">
          <p>© 2026 WhatisHyped Media LLC. All rights reserved.</p>
          <p className="mt-2">
            Questions about this agreement? Contact us at{" "}
            <a href="mailto:support@wihmedia.com" className="text-blue-600 hover:underline">
              support@wihmedia.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ServiceAgreement;
